import { useContext } from "react";
import { useForm } from "react-final-form";

import {
  Card,
  FontIcon,
  Heading,
  Link,
  Separator,
  Stack,
  Text,
  TooltipHost
} from "@bps/fluent-ui";
import { getAccidentAndEmploymentDetailsViewValues } from "@modules/acc/screens/claim/components/utils.ts";
import { ClaimContext } from "@modules/acc/screens/claim/context/ClaimContext.ts";
import { ClaimFormValues } from "@shared-types/acc/claim-form-values.type.ts";
import { IRootStore } from "@shared-types/root/root-store.interface.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import {
  DataFetcher,
  withFetch
} from "@ui-components/data-fetcher/DataFetcher.tsx";

import {
  AccidentAndEmploymentDetailsViewValues,
  LabeledTextFields
} from "../types/claim.types.ts";
import {
  ClaimFormLabels,
  ClaimsCardIds,
  ClaimSectionHeadings
} from "./ClaimFormEnums.ts";
import { ViewField } from "./ViewField.tsx";

const AccidentAndEmploymentDetailsViewSectionBase: React.FC<{
  accidentAndEmploymentDetailsValues: AccidentAndEmploymentDetailsViewValues;
}> = ({ accidentAndEmploymentDetailsValues }) => {
  const { claim } = useContext(ClaimContext);
  const { acc, notification } = useStores();

  // GIVEN the Date of Injury has been updated via the link - use just the date not include the time portion
  // THEN the form displays the new value with an inline indicator in the format "<new value> was (<old value>)", store/show only last change
  const hasUpdatedDate =
    accidentAndEmploymentDetailsValues.accidentDateUpdatedFormatted &&
    accidentAndEmploymentDetailsValues.accidentDateFormatted !==
      accidentAndEmploymentDetailsValues.accidentDateUpdatedFormatted;

  const handleClickInjuryDate = async () => {
    await acc.queueClaimStatus(claim.id);
    notification.success("Claim status has been queued.");
  };

  const fields: LabeledTextFields[] = [
    {
      renderInGroup: true,
      labeledTexts: [
        {
          noSemicolon: true,
          label: ClaimFormLabels.date,
          children: (() => {
            if (!claim.updatedBy) return null;

            // Helper to render the actual Tooltip + Text
            const renderDateInfo = () => {
              const dateTooltip = hasUpdatedDate
                ? `Updated on ${claim.updatedDate?.toDayDefaultFormat()}`
                : undefined;

              const dateShown = hasUpdatedDate
                ? `${accidentAndEmploymentDetailsValues.accidentDateUpdatedFormatted} (was ${accidentAndEmploymentDetailsValues.accidentDateFormatted})`
                : accidentAndEmploymentDetailsValues.accidentDateFormatted;

              return (
                <TooltipHost content={dateTooltip}>
                  <span>{dateShown} </span>
                  {dateTooltip && <FontIcon iconName="Info" />}
                </TooltipHost>
              );
            };
            return renderDateInfo();
          })()
        },
        {
          noSemicolon: true,
          label: ClaimFormLabels.sceneOfAccident,
          children: accidentAndEmploymentDetailsValues.accidentSceneText
        },
        {
          noSemicolon: true,
          label: ClaimFormLabels.accidentLocation,
          children: accidentAndEmploymentDetailsValues.accidentLocationText
        }
      ]
    },
    {
      labeledTexts: [
        {
          noSemicolon: true,
          label: ClaimFormLabels.describeTheAccident,
          children: accidentAndEmploymentDetailsValues.causeOfAccident
        },
        {
          noSemicolon: true,
          label: ClaimFormLabels.involvesVehicle,
          children: accidentAndEmploymentDetailsValues.involvesVehicleText
        },
        {
          noSemicolon: true,
          label: ClaimFormLabels.medicalTreatmentInjury,
          children:
            accidentAndEmploymentDetailsValues.medicalTreatmentInjuryText
        },
        {
          noSemicolon: true,
          label: ClaimFormLabels.workInjury,
          children: accidentAndEmploymentDetailsValues.workInjuryText
        }
      ]
    },
    {
      renderInGroup: true,
      labeledTexts: [
        {
          noSemicolon: true,
          label: ClaimFormLabels.sportingInjury,
          children: accidentAndEmploymentDetailsValues.sportingInjuryText
        },
        {
          noSemicolon: true,
          label: ClaimFormLabels.sport,
          children: accidentAndEmploymentDetailsValues.sportText
        }
      ]
    },
    {
      renderInGroup: true,
      labeledTexts: [
        {
          noSemicolon: true,
          label: ClaimFormLabels.paidEmployment,
          children: accidentAndEmploymentDetailsValues.inPaidEmploymentText
        },
        {
          noSemicolon: true,
          label: ClaimFormLabels.occupation,
          children: accidentAndEmploymentDetailsValues.occupationText
        }
      ]
    },
    {
      renderInGroup: true,
      labeledTexts: [
        {
          noSemicolon: true,
          label: ClaimFormLabels.employmentStatus,
          children: accidentAndEmploymentDetailsValues.employmentStatusText
        },
        {
          noSemicolon: true,
          label: ClaimFormLabels.workType,
          children: accidentAndEmploymentDetailsValues.workTypeText
        }
      ]
    },
    {
      labeledTexts: [
        {
          noSemicolon: true,
          label: ClaimFormLabels.businessName,
          children: accidentAndEmploymentDetailsValues.employerName
        }
      ]
    },
    {
      renderInGroup: true,
      labeledTexts: [
        {
          noSemicolon: true,
          label: ClaimFormLabels.address,
          children: accidentAndEmploymentDetailsValues.employerAddressFormatted
        },
        {
          noSemicolon: true,
          label: ClaimFormLabels.addressType,
          children: accidentAndEmploymentDetailsValues.employerAddressTypeText
        }
      ]
    }
  ];

  return (
    <Card
      heading={
        <Stack
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
          tokens={{ childrenGap: 10 }}
        >
          <span>{ClaimSectionHeadings.accidentAndEmploymentDetails}</span>
        </Stack>
      }
      button={
        <Text>
          <Link onClick={handleClickInjuryDate}>Get injury date from ACC</Link>
        </Text>
      }
      headingLevel="section-heading"
      iconName="BpMedCare"
      id={ClaimsCardIds.accidentAndEmploymentDetails}
    >
      <Stack tokens={{ childrenGap: 8 }}>
        <Heading variant="section-heading-light">Accident</Heading>
        <ViewField {...fields[0]} />
        <ViewField {...fields[1]} />
        <ViewField {...fields[2]} />
      </Stack>

      <Separator />

      <Stack tokens={{ childrenGap: 8 }}>
        <Heading variant="section-heading-light">Employment</Heading>
        <ViewField {...fields[3]} />
        <ViewField {...fields[4]} />
        <ViewField {...fields[5]} />
        <ViewField {...fields[6]} />
      </Stack>
    </Card>
  );
};

const AccidentAndEmploymentDetailsDataFetcher = () => {
  const values = useForm<ClaimFormValues>().getState().values;

  return (
    <DataFetcher
      fetch={(root: IRootStore) =>
        getAccidentAndEmploymentDetailsViewValues({ values, root })
      }
    >
      {(
        accidentAndEmploymentDetails: AccidentAndEmploymentDetailsViewValues
      ) => (
        <AccidentAndEmploymentDetailsViewSectionBase
          accidentAndEmploymentDetailsValues={accidentAndEmploymentDetails}
        />
      )}
    </DataFetcher>
  );
};

export const AccidentAndEmploymentDetailsViewSection = withFetch(
  x => [
    x.acc.ref.sports.load(),
    x.acc.ref.sports.load(),
    x.acc.ref.occupations.load(),
    x.acc.ref.employmentStatuses.load(),
    x.acc.ref.workTypes.load(),
    x.acc.ref.accidentScenes.load(),
    x.acc.ref.accidentLocations.load()
  ],
  AccidentAndEmploymentDetailsDataFetcher
);
